import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useApiClient } from "../../hooks/use-api-client.ts";
import type { LocationSummary } from "../../api/client.ts";

export function TransferStart() {
  const navigate = useNavigate();
  const api = useApiClient();
  const [locations, setLocations] = useState<LocationSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [dest, setDest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .listLocations()
      .then((r) => {
        if (active) setLocations(r.locations);
      })
      .catch((e: unknown) => {
        if (active) setLoadError(e instanceof Error ? e.message : "Could not load locations");
      });
    return () => {
      active = false;
    };
  }, [api]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!source || !dest || !api) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await api.startTransfer({ sourceLocationId: Number(source), destLocationId: Number(dest) });
      void navigate({ to: "/transfer/$ref", params: { ref: result.pickingRef } });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to start transfer");
      setSubmitting(false);
    }
  }

  const selectClass =
    "w-full rounded-xl border border-gray-700 bg-gray-800 px-4 py-4 text-lg text-white focus:border-purple-500 focus:outline-none";

  return (
    <div className="flex flex-1 flex-col p-6">
      <button
        onClick={() => void navigate({ to: "/workflow" })}
        className="mb-6 flex w-fit items-center gap-1 text-sm text-gray-400 active:text-white"
      >
        ← Back
      </button>
      <h2 className="mb-2 text-2xl font-bold">Stock Transfer</h2>
      <p className="mb-8 text-gray-400">Select source and destination locations</p>

      {errorMsg && (
        <div className="mb-4 rounded-xl bg-red-900/40 px-4 py-2 text-sm text-red-300">{errorMsg}</div>
      )}
      {loadError && (
        <div className="mb-4 rounded-xl border border-red-700 bg-red-950/40 p-4 text-sm text-red-300">
          Could not load locations: {loadError}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">
            Source location
          </label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            disabled={!locations}
            className={selectClass}
          >
            <option value="">{locations ? "Select source…" : "Loading…"}</option>
            {locations?.map((l) => (
              <option key={l.id} value={String(l.id)}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-widest text-gray-500">
            Destination location
          </label>
          <select
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            disabled={!locations}
            className={selectClass}
          >
            <option value="">{locations ? "Select destination…" : "Loading…"}</option>
            {locations?.map((l) => (
              <option key={l.id} value={String(l.id)}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={!source || !dest || source === dest || submitting}
          className="w-full rounded-xl bg-purple-600 py-4 text-lg font-semibold disabled:opacity-40 active:bg-purple-700"
        >
          {submitting ? "Starting…" : "Start Transfer"}
        </button>
        {source && dest && source === dest && (
          <p className="text-center text-sm text-amber-400">Source and destination must differ.</p>
        )}
      </form>
    </div>
  );
}
